import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { cn } from "@-/com.npmjs.cn";
import {
  REVIEW_RATING,
  type ReviewRating,
} from "@-/dev.improve.haniverse.lib.cards.user-card.fsrs";
import { ActivityIndicator, View } from "@-/com.npmjs.react-native";

const REVIEW_RATING_OPTION_LIST = [
  { label: "Again", rating: REVIEW_RATING.Again },
  { label: "Hard", rating: REVIEW_RATING.Hard },
  { label: "Good", rating: REVIEW_RATING.Good },
  { label: "Easy", rating: REVIEW_RATING.Easy },
] as const satisfies readonly { label: string; rating: ReviewRating }[];

/**
 * The rating row shown once a sentence drill ends.
 *
 * A deliberate sibling of the deck's floating rating buttons rather than a reuse: those live in
 * the stationary action area with FSRS interval previews, while this row sits INSIDE the card
 * once the sentence has been revealed in place. It keeps the same labels and the same
 * `review-rating-*` testIDs so the flows and muscle memory carry over.
 *
 * `Again` is preselected because that is what the revealed word scores by default — the user is
 * being asked to confirm or override one word's grade, not to grade the sentence.
 */
export function DrillPageSentenceRating({
  onRate,
  pendingRating,
}: {
  onRate: (rating: ReviewRating) => void;
  pendingRating: ReviewRating | null;
}) {
  return (
    <View className="w-full gap-2 px-4 pb-2">
      <Text className="text-center text-sm leading-tight" colorVariant="muted">
        How well did you know this word?
      </Text>
      <View className="flex-row items-stretch gap-1">
        {REVIEW_RATING_OPTION_LIST.map((ratingOption) => {
          const isDefault = ratingOption.rating === REVIEW_RATING.Again;
          const isPending = pendingRating === ratingOption.rating;
          return (
            <View className="min-w-0 flex-1" key={ratingOption.rating}>
              <Button
                accessibilityLabel={ratingOption.label}
                accessibilityState={{ busy: isPending, disabled: pendingRating !== null }}
                className={cn(
                  "relative h-9 w-full items-center justify-center rounded-xs px-1 py-0",
                  isDefault && "border-2 border-primary",
                )}
                isLoading={pendingRating !== null}
                onPress={() => {
                  onRate(ratingOption.rating);
                }}
                testID={`review-rating-${ratingOption.label.toLowerCase()}`}
                variant="outline"
              >
                <Text
                  className={cn(
                    "text-xs",
                    isDefault && "font-semibold",
                    pendingRating !== null && (isPending ? "opacity-25" : "opacity-50"),
                  )}
                >
                  {ratingOption.label}
                </Text>
                {isPending ? (
                  <View
                    className="absolute inset-0 items-center justify-center"
                    pointerEvents="none"
                  >
                    <ActivityIndicator size="small" />
                  </View>
                ) : null}
              </Button>
            </View>
          );
        })}
      </View>
    </View>
  );
}
